<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import { computed, ref } from "vue";
import EditableElement from "@/views/page/EditableElement.vue";
import Background from "@/views/blocks/banner/components/background/Background.vue";
import DismissButton from "@/views/blocks/banner/components/dismiss-button/DismissButton.vue";
import Title from "@/views/blocks/banner/components/title/Title.vue";
import Description from "@/views/blocks/banner/components/description/Description.vue";
import DotIcon from "@/views/blocks/banner/components/dot-icon/DotIcon.vue";
import SecondaryButton from "@/views/blocks/shared/section/buttons/secondary-button/SecondaryButton.vue";
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import { FloatingAtBottomProps } from "./FloatingAtBottomProps";

const props = defineProps<{
    block: PageBlock<FloatingAtBottomProps>;
    pageColors: CssVarColors;
    isPreview: boolean;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);

const isVisible = ref(true);
</script>

<template>
    <div
        v-if="isVisible"
        :class="!isPreview ? 'fixed bottom-0 left-0 right-0 z-50 sm:px-6 sm:pb-5 lg:px-8' : ''"
    >
        <Section
            :id="block.webId"
            :background-color="styleValues.background"
            class="md:rounded-lg md:overflow-hidden"
        >
            <Background
                :background-color="styleValues.background"
                :is-with-effect="true"
                class="justify-between"
            >
                <div class="flex flex-wrap items-center gap-x-2 gap-y-2">
                    <p class="text-sm/6">
                        <EditableElement
                            v-if="!dataValues.title.isHidden"
                            :blockId="block.id"
                            field-name="title"
                        >
                            <Title
                                :text="dataValues.title.data.text"
                                :text-color="styleValues.text"
                            />
                        </EditableElement>
                        <DotIcon
                            v-if="!dataValues.title.isHidden && !dataValues.description.isHidden"
                            :style="{
                                color: styleValues.text,
                            }"
                        />
                        <EditableElement
                            v-if="!dataValues.description.isHidden"
                            :blockId="block.id"
                            field-name="description"
                        >
                            <Description
                                :text="dataValues.description.data.text"
                                :text-color="styleValues.text"
                            />
                        </EditableElement>
                    </p>
                    <EditableElement
                        v-if="!dataValues.link.isHidden"
                        :blockId="block.id"
                        field-name="link"
                    >
                        <SecondaryButton
                            :label="dataValues.link.data.label"
                            :href="dataValues.link.data.href"
                            :text-color="styleValues.text"
                            :show-arrow="true"
                            class="text-sm font-semibold"
                        />
                    </EditableElement>
                </div>
                <div class="flex items-center justify-center">
                    <DismissButton :accent-color="styleValues.text" @dismiss="isVisible = false" />
                </div>
            </Background>
        </Section>
    </div>
</template>
